<h2 mat-dialog-title>
  Dashboard vs. CMS Measure Data
  <span class="dialog-subtitle">Submission #{{ data.submissionId }}</span>
</h2>

<mat-dialog-content class="check-dialog-content">
  <div class="loading-row" *ngIf="isLoading">
    <mat-spinner diameter="32"></mat-spinner>
    <span>Checking dashboard, sent, and CMS values...</span>
  </div>

  <div class="alert alert-danger" *ngIf="errorMessage">
    <mat-icon>error_outline</mat-icon>
    <p>{{ errorMessage }}</p>
  </div>

  <ng-container *ngIf="result as result">
    <div class="alert alert-danger" *ngIf="result.dashboardError">
      <mat-icon>error_outline</mat-icon>
      <div>
        <strong>Dashboard values unavailable</strong>
        <p>{{ result.dashboardError }}</p>
      </div>
    </div>
    <div class="alert alert-danger" *ngIf="result.sentError">
      <mat-icon>error_outline</mat-icon>
      <div>
        <strong>Sent values unavailable</strong>
        <p>{{ result.sentError }}</p>
      </div>
    </div>
    <div class="alert alert-danger" *ngIf="result.cmsError">
      <mat-icon>error_outline</mat-icon>
      <div>
        <strong>CMS score unavailable</strong>
        <p>{{ result.cmsError }}</p>
      </div>
    </div>

    <div class="check-summary-row">
      <p class="check-summary">
        {{ result.mismatchCount }} {{ result.mismatchCount === 1 ? 'mismatch' : 'mismatches' }}        <span class="summary-meta" *ngIf="result.qppSubmissionId; else notFinalized">
          · Sent {{ result.sentDate ? (result.sentDate | date: 'MMM d, y, h:mm a') : '—' }}
          · QPP ID {{ result.qppSubmissionId }}
        </span>
        <ng-template #notFinalized>
          <span class="summary-meta">· Not finalized yet</span>
        </ng-template>
      </p>
      <div class="view-options">
        <mat-checkbox
          [checked]="shouldShowMismatchesFirst"
          (change)="onMismatchesFirstChange($event.checked)">
          Mismatches first
        </mat-checkbox>
        <mat-checkbox
          [checked]="shouldShowRawJson"
          [disabled]="isLoading"
          (change)="onRawJsonChange($event.checked)">
          Show raw JSON
        </mat-checkbox>
      </div>
    </div>

    <div class="check-table-wrapper" *ngIf="displayedRows.length > 0">
      <table class="check-table">
        <thead>
          <tr>
            <th rowspan="2">Measure</th>
            <th rowspan="2">Stratum</th>
            <th rowspan="2">Type</th>
            <th colspan="2" class="group-header group-start">Dashboard now</th>
            <th colspan="2" class="group-header group-start">Sent to CMS</th>
            <th colspan="2" class="group-header group-start">CMS recorded</th>
            <th rowspan="2" class="group-start">Status</th>
          </tr>
          <tr>
            <th class="group-start">Num / Den</th>
            <th>Rate</th>
            <th class="group-start">Num / Den</th>
            <th>Rate</th>
            <th class="group-start">Num / Den</th>
            <th>Rate</th>
          </tr>
        </thead>
        <tbody>
          <ng-container *ngFor="let displayRow of displayedRows">
            <tr
              [class.mismatch-row]="!displayRow.row.isMatch"
              [class.has-sub-row]="(!displayRow.row.isMatch && displayRow.row.issues.length) || (isExpanded(displayRow) && hasDetails(displayRow))">
              <td>
                <div class="measure-cell">
                  <button
                    *ngIf="hasDetails(displayRow); else expandSpacer"
                    type="button"
                    class="expand-button"
                    [attr.aria-expanded]="isExpanded(displayRow)"
                    [attr.aria-label]="(isExpanded(displayRow) ? 'Hide' : 'Show') + ' details for ' + getMeasureLabel(displayRow.row)"
                    (click)="toggleExpanded(displayRow)">
                    <mat-icon>{{ isExpanded(displayRow) ? 'expand_more' : 'chevron_right' }}</mat-icon>
                  </button>
                  <ng-template #expandSpacer><span class="expand-spacer"></span></ng-template>
                  <span>{{ getMeasureLabel(displayRow.row) }}</span>
                  <span class="inverse-badge" *ngIf="displayRow.row.isInverted">Inverse</span>
                </div>
                <span class="measure-id" *ngIf="hasSeparateMeasureId(displayRow.row)">{{ displayRow.row.measureId }}</span>
              </td>
              <td>{{ displayRow.row.stratum ?? '—' }}</td>
              <td>{{ displayRow.row.measureType ?? '—' }}</td>
              <td class="group-start">{{ formatFraction(displayRow.row.dashboardNumerator, displayRow.row.dashboardDenominator) }}</td>
              <td>{{ formatRate(displayRow.row.dashboardRate) }}</td>
              <td class="group-start">{{ formatFraction(displayRow.row.sentNumerator, displayRow.row.sentDenominator) }}</td>
              <td>{{ formatRate(displayRow.row.sentRate) }}</td>
              <td class="group-start">{{ formatFraction(displayRow.row.cmsNumerator, displayRow.row.cmsDenominator) }}</td>
              <td>{{ formatRate(displayRow.row.cmsRate) }}</td>
              <td class="group-start status-cell">
                <mat-icon *ngIf="displayRow.row.isMatch" class="status-ok">check_circle</mat-icon>
                <mat-icon *ngIf="!displayRow.row.isMatch" class="status-bad">error</mat-icon>
              </td>
            </tr>
            <tr
              *ngIf="!displayRow.row.isMatch && displayRow.row.issues.length"
              class="mismatch-row issues-row"
              [class.has-sub-row]="isExpanded(displayRow) && hasDetails(displayRow)">
              <td colspan="10">
                <ul class="issues-list">
                  <li *ngFor="let issue of displayRow.row.issues">{{ issue }}</li>
                </ul>
              </td>
            </tr>
            <tr *ngIf="isExpanded(displayRow) && hasDetails(displayRow)" class="details-row">
              <td colspan="10">
                <div class="details-section" *ngIf="displayRow.cmsProcessingStatus">
                  <strong>CMS status</strong>
                  <span class="details-value">{{ displayRow.cmsProcessingStatus }}</span>
                </div>
                <div class="details-section" *ngIf="displayRow.cmsMessages.length > 0">
                  <strong>CMS messages</strong>
                  <ul class="messages-list">
                    <li *ngFor="let message of displayRow.cmsMessages">
                      <span class="message-key">{{ message[0] }}:</span> {{ message[1] }}
                    </li>
                  </ul>
                </div>
                <div class="details-section" *ngIf="shouldShowRawJson && (displayRow.prettySentJson || displayRow.prettyCmsJson)">
                  <div class="raw-json-grid">
                    <div>
                      <strong>Sent to CMS</strong>
                      <pre class="raw-json" *ngIf="displayRow.prettySentJson; else notAvailable">{{ displayRow.prettySentJson }}</pre>
                    </div>
                    <div>
                      <strong>CMS response</strong>
                      <pre class="raw-json" *ngIf="displayRow.prettyCmsJson; else notAvailable">{{ displayRow.prettyCmsJson }}</pre>
                    </div>
                  </div>
                </div>
                <ng-template #notAvailable><p class="not-available">Not available</p></ng-template>
              </td>
            </tr>
          </ng-container>
        </tbody>
      </table>
    </div>
  </ng-container>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-stroked-button (click)="onDownload()" [disabled]="isLoading || isDownloading">
    {{ isDownloading ? 'Downloading...' : 'Download JSON' }}
  </button>
  <button mat-stroked-button (click)="loadCheck()" [disabled]="isLoading">Refresh</button>
  <button mat-flat-button color="primary" (click)="onClose()">Close</button>
</mat-dialog-actions>
